// Copyright (c) Microsoft. All rights reserved.

import {
    ToggleButton,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
    Tooltip,
    Toast,
    ToastTitle,
    ToastBody,
    useToastController,
    Button,
} from '@fluentui/react-components';
import {
    ChevronDown20Regular,
    ChevronUp20Regular,
    ThumbDislikeFilled,
    ThumbLikeFilled,
    Copy16Regular,
    ThumbLike16Regular,
    ThumbDislike16Regular,
    ThumbLike16Filled,
    ThumbDislike16Filled,
    // DocumentText16Regular,
    // DocumentText16Filled,
    Star16Regular,
} from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import React, { useCallback, useState } from 'react';
import { useChat } from '../../../libs/hooks/useChat';
import { useCopyToClipboard } from '../../../libs/hooks/useCopyToClipboard';
import { AuthorRoles, ChatMessageType, IChatMessage, UserFeedback } from '../../../libs/models/ChatMessage';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { DefaultChatUser, FeatureKeys } from '../../../redux/features/app/AppState';
import { Breakpoints, customTokens } from '../../../styles';
//import { PromptDialog } from '../prompt-dialog/PromptDialog';
import { TypingIndicator } from '../typing-indicator/TypingIndicator';
import { ThinkingIndicator } from '../shared/ThinkingIndicator';
import * as utils from './../../utils/TextUtils';
import { ChatHistoryDocumentContent } from './ChatHistoryDocumentContent';
import { ChatHistoryTextContent } from './ChatHistoryTextContent';
import { CitationCards } from './CitationCards';
import { UserFeedbackActions } from './UserFeedbackActions';
import { FeedbackModal } from '../../FeedbackModal';
import { FeedbackResponse, FeedbackService } from '../../../libs/services/FeedbackService';
import { useAgentSafe } from '../../../libs/context/AgentContext';
import { AuthHelper } from '../../../libs/auth/AuthHelper';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '75%',
        ...shorthands.borderRadius(customTokens.borderRadiusMedium),
        ...Breakpoints.small({
            maxWidth: '100%',
        }),
        ...shorthands.gap(customTokens.spacingHorizontalS),
    },
    debug: {
        position: 'absolute',
        top: '-4px',
        right: '-4px',
    },
    alignEnd: {
        alignSelf: 'flex-end',
    },
    persona: {
        paddingTop: customTokens.spacingVerticalS,
    },
    item: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.padding(customTokens.spacingVerticalM),
        borderRadius: '2px 20px 20px 20px',
        gap: '10px',
        color: tokens.colorNeutralForeground1,
        maxWidth: '100%',
        minWidth: 0,
    },
    me: {
        ...shorthands.padding(customTokens.spacingVerticalM),
        backgroundColor: customTokens.colorMeBackground,
        borderRadius: '20px 20px 2px 20px',
        gap: '10px',
        color: tokens.colorNeutralForegroundStaticInverted,
        maxWidth: '100%',
        wordBreak: 'break-word' as const,
    },
    time: {
        color: customTokens.colorNeutralForeground3,
        fontSize: customTokens.fontSizeBase200,
        fontWeight: 400,
    },
    header: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap(customTokens.spacingHorizontalL),
    },
    canvas: {
        width: '100%',
        textAlign: 'center',
    },
    image: {
        maxWidth: '250px',
    },
    blur: {
        filter: 'blur(5px)',
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        marginTop: customTokens.spacingVerticalS,
        marginBottom: customTokens.spacingVerticalS,
        ...shorthands.gap(customTokens.spacingHorizontalL),
    },
    citationButton: {
        marginRight: 'auto',
    },
    rlhf: {
        marginLeft: 'auto',
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap('4px'),
    },
    actionButton: {
        minWidth: '24px',
        height: '24px',
        ...shorthands.border('none'),
        backgroundColor: 'transparent',
        ...shorthands.borderRadius('4px'),
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            transform: 'scale(1.05)',
        },
        '&:active': {
            transform: 'scale(0.95)',
        },
    },
    actionButtonActive: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground2,
        '&:hover': {
            backgroundColor: tokens.colorBrandBackground2Hover,
        },
    },
    toasterContainer: {
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
    },
    logsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: customTokens.spacingVerticalS,
        ...shorthands.padding(customTokens.spacingVerticalM),
        backgroundColor: customTokens.colorContentWrapper,
        ...shorthands.borderRadius(customTokens.borderRadiusLarge),
        ...shorthands.border('1px', 'solid', customTokens.colorBorder),
        maxHeight: '400px',
        overflowY: 'auto',
        ...shorthands.gap(customTokens.spacingVerticalS),
    },
    logsActionButton: {
        minWidth: '24px',
        height: '24px',
        ...shorthands.border('none'),
        backgroundColor: 'transparent',
        ...shorthands.borderRadius('4px'),
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    logItem: {
        ...shorthands.padding(customTokens.spacingVerticalS, customTokens.spacingHorizontalM),
        backgroundColor: customTokens.colorChatRoomBackground,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.borderLeft('3px', 'solid', customTokens.colorMeBackground),
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('4px'),
    },
    logHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '4px',
    },
    logAgentName: {
        fontWeight: 600,
        fontSize: customTokens.fontSizeBase300,
        color: customTokens.colorMeBackground,
    },
    logTimestamp: {
        fontSize: customTokens.fontSizeBase200,
        color: customTokens.colorTextDisabled,
    },
    logContent: {
        fontSize: customTokens.fontSizeBase300,
        color: customTokens.colorCustomText,
        lineHeight: '1.5',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    logType: {
        fontSize: customTokens.fontSizeBase200,
        color: customTokens.colorTextDisabled,
        fontStyle: 'italic',
    },
});

interface ChatHistoryItemProps {
    messages: IChatMessage[];
    messageIndex: number;
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ messages, messageIndex }) => {
    const classes = useClasses();
    const chat = useChat();
    const { copy } = useCopyToClipboard();
    const { dispatchToast } = useToastController();
    // const { canViewAgentLogs } = useUserRoles();
    const { instance, inProgress } = useMsal();

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
    const agentContext = useAgentSafe();

    // Use first message for author/user metadata
    const message = messages[0];
    
    const [showCitationCards, setShowCitationCards] = useState(false);
    const [isLiked, setIsLiked] = useState(false);
    const [isDisliked, setIsDisliked] = useState(false);
    // const [showLogs, setShowLogs] = useState(false);
    
    // Feedback modal state
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [existingFeedback, setExistingFeedback] = useState<FeedbackResponse | null>(null);
    const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);

    const feedbackService = React.useMemo(() => new FeedbackService(), []);

    const isDefaultUser = message.userId === DefaultChatUser.id;
    const isMe = isDefaultUser || (message.authorRole === AuthorRoles.User && message.userId === activeUserInfo?.id);
    const isBot = message.authorRole === AuthorRoles.Bot;
    const user = isDefaultUser
        ? DefaultChatUser
        : chat.getChatUserById(message.userName, selectedId, conversations[selectedId].users);
    const fullName = user?.fullName ?? message.userName;

    // Load existing feedback when message has an ID
    const loadExistingFeedback = useCallback(async () => {
        if (!message.id) return;
        
        setIsLoadingFeedback(true);
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            const feedback = await feedbackService.getFeedbackByMessageId(message.id, accessToken);
            setExistingFeedback(feedback);
        } catch (error) {
            console.error('Error loading existing feedback:', error);
        } finally {
            setIsLoadingFeedback(false);
        }
    }, [message.id, instance, inProgress, feedbackService]);

    // Event handlers for action buttons
    const handleCopyClick = async () => {
        const success = await copy(message.content);
        if (success) {
            dispatchToast(
                <Toast>
                    <ToastTitle>Kopyalandı!</ToastTitle>
                    <ToastBody>Yanıt panoya kopyalandı</ToastBody>
                </Toast>,
                { intent: 'success', timeout: 2000 },
            );
        } else {
            dispatchToast(
                <Toast>
                    <ToastTitle>Hata!</ToastTitle>
                    <ToastBody>Kopyalama işlemi başarısız oldu</ToastBody>
                </Toast>,
                { intent: 'error', timeout: 2000 },
            );
        }
    };

    const handleFeedbackClick = useCallback(async () => {
        await loadExistingFeedback();
        setIsFeedbackModalOpen(true);
    }, [loadExistingFeedback]);

    const handleFeedbackModalClose = useCallback(() => {
        setIsFeedbackModalOpen(false);
        // Refresh feedback after modal closes to update UI state
        void loadExistingFeedback();
    }, [loadExistingFeedback]);

    const handleLikeClick = () => {
        if (isDisliked) {
            setIsDisliked(false);
        }
        setIsLiked(!isLiked);
    };

    const handleDislikeClick = () => {
        if (isLiked) {
            setIsLiked(false);
        }
        setIsDisliked(!isDisliked);
    };

    // const handleLogsClick = () => {
    //     setShowLogs(!showLogs);
    // };

    // Dummy log data - will be replaced with real data later
    /* const dummyLogs = [
        {
            agentName: 'Agent-1',
            content: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry\'s standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum.',
            timestamp: new Date().toISOString(),
            agentLogType: '1',
        },
        {
            agentName: 'Agent-2',
            content: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
            timestamp: new Date(Date.now() - 5000).toISOString(),
            agentLogType: '2',
        },
        {
            agentName: 'Agent-1',
            content: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry',
            timestamp: new Date(Date.now() - 10000).toISOString(),
            agentLogType: '1',
        },
    ]; */

    /*  const avatar: AvatarProps = isBot
        ? { image: { src: conversations[selectedId].botProfilePicture } }
        : isDefaultUser
          ? { idForColor: selectedId, color: 'colorful' }
          : { name: fullName, color: 'colorful' };
 */
    // Build content for all messages in the group inside a single bubble
    const showShowRLHFMessage =
        features[FeatureKeys.RLHF].enabled &&
        message.userFeedback === UserFeedback.Requested &&
        messageIndex === conversations[selectedId].messages.length - 1 &&
        message.userId === 'Bot';

    const content = (
        <>
            {messages.map((msg, idx) => {
                if (msg.type === ChatMessageType.Document) {
                    return <ChatHistoryDocumentContent key={idx} isMe={isMe} message={msg} />;
                }
                return isBot && msg.content.length === 0 ? (
                    <TypingIndicator key={idx} />
                ) : (
                    <ChatHistoryTextContent key={idx} message={msg} isMe={isMe} />
                );
            })}
        </>
    );

    // Collect citations from all messages in the group
    const messageCitations = messages.flatMap((msg) => msg.citations ?? []);
    const showMessageCitation = messageCitations.length > 0;
    const showExtra = showMessageCitation || showShowRLHFMessage || showCitationCards;

    return (
        <div
            className={isMe ? mergeClasses(classes.root, classes.alignEnd) : classes.root}
            // The following data attributes are needed for CI and testing
            data-testid={`chat-history-item-${messageIndex}`}
            data-username={fullName}
            data-content={utils.formatChatTextContent(messages.map((m) => m.content).join(' '))}
        >
            {/*  {
                <Persona
                    className={classes.persona}
                    avatar={avatar}
                    presence={
                        !features[FeatureKeys.SimplifiedExperience].enabled && !isMe
                            ? { status: 'available' }
                            : undefined
                    }
                />
            } */}
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */}
            {isBot && message.agentPanels && message.agentPanels.length > 0 && (
                <ThinkingIndicator panels={message.agentPanels} defaultCollapsed />
            )}
            <div className={isMe ? classes.me : classes.item}>
                <div className={classes.header}>
                    {/* {!isMe && <Text weight="semibold">{fullName}</Text>} */}
                    {/*     <Text className={classes.time}>{timestampToDateString(message.timestamp, true)}</Text> */}
                    {/* {isBot && <PromptDialog message={message} />} */}
                    {/*  {isBot && message.prompt && (
                        <Tooltip content={messagedCopied ? 'Copied' : 'Copy text'} relationship="label">
                            <Button
                                icon={messagedCopied ? <ClipboardTask20Regular /> : <Clipboard20Regular />}
                                appearance="transparent"
                                onClick={() => {
                                    void copyOnClick();
                                }}
                            />
                        </Tooltip>
                    )} */}
                </div>
                {content}
                {showExtra && (
                    <div className={classes.controls}>
                        {showMessageCitation && (
                            <ToggleButton
                                appearance="subtle"
                                checked={showCitationCards}
                                className={classes.citationButton}
                                icon={showCitationCards ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                                iconPosition="after"
                                onClick={() => {
                                    setShowCitationCards(!showCitationCards);
                                }}
                                size="small"
                            >
                                {`${messageCitations.length} ${
                                    messageCitations.length === 1 ? 'citation' : 'citations'
                                }`}
                            </ToggleButton>
                        )}
                        {showShowRLHFMessage && (
                            <div className={classes.rlhf}>{<UserFeedbackActions messageIndex={messageIndex} />}</div>
                        )}
                        {showCitationCards && <CitationCards message={message} />}
                    </div>
                )}
            </div>
            {features[FeatureKeys.RLHF].enabled && message.userFeedback === UserFeedback.Positive && (
                <ThumbLikeFilled color="gray" />
            )}
            {features[FeatureKeys.RLHF].enabled && message.userFeedback === UserFeedback.Negative && (
                <ThumbDislikeFilled color="gray" />
            )}

            {isBot && (
                <>
                    <div className={classes.actions}>
                        <Tooltip content="Kopyala" relationship="label" withArrow>
                            <Button
                                appearance="subtle"
                                className={classes.actionButton}
                                icon={<Copy16Regular />}
                                onClick={() => void handleCopyClick()}
                                size="small"
                            />
                        </Tooltip>

                        {(!isDisliked || isLiked) && (
                            <Tooltip
                                content={isLiked ? 'Geri bildirimi kaldır' : 'Bu yanıtı beğen'}
                                relationship="label"
                                withArrow
                            >
                                <Button
                                    appearance="subtle"
                                    className={mergeClasses(
                                        classes.actionButton,
                                        isLiked && classes.actionButtonActive,
                                    )}
                                    icon={isLiked ? <ThumbLike16Filled /> : <ThumbLike16Regular />}
                                    onClick={handleLikeClick}
                                    size="small"
                                />
                            </Tooltip>
                        )}

                        {(!isLiked || isDisliked) && (
                            <Tooltip
                                content={isDisliked ? 'Geri bildirimi kaldır' : 'Bu yanıttan hoşlanmadım'}
                                relationship="label"
                                withArrow
                            >
                                <Button
                                    appearance="subtle"
                                    className={mergeClasses(
                                        classes.actionButton,
                                        isDisliked && classes.actionButtonActive,
                                    )}
                                    icon={isDisliked ? <ThumbDislike16Filled /> : <ThumbDislike16Regular />}
                                    onClick={handleDislikeClick}
                                    size="small"
                                />
                            </Tooltip>
                        )}

                        {/* Detailed feedback button - only show if message has an ID and agent context is available */}
                        {message.id && agentContext?.selectedAgentId && (
                            <Tooltip content={existingFeedback ? "Geri bildirimi düzenle" : "Detaylı geri bildirim ver"} relationship="label" withArrow>
                                <Button
                                    appearance="subtle"
                                    className={mergeClasses(classes.actionButton, existingFeedback ? classes.actionButtonActive : undefined)}
                                    icon={<Star16Regular />}
                                    onClick={() => void handleFeedbackClick()}
                                    disabled={isLoadingFeedback}
                                    size="small"
                                />
                            </Tooltip>
                        )}
                        {/* Logları Görüntüle butonu geçici olarak devre dışı */}
                        {/* {canViewAgentLogs && (
                                <Button
                                    appearance="outline"
                                    className={mergeClasses(classes.logsActionButton, showLogs && classes.actionButtonActive)}
                                    icon={showLogs ? <DocumentText16Filled /> : <DocumentText16Regular />}
                                    onClick={handleLogsClick}
                                    size="small"
                                >
                                    Logları {showLogs ? "Gizle" : "Görüntüle"}
                                </Button>
                        )} */}
                    </div>
                    {/* Logs panel - temporarily disabled */}
                    {/* {showLogs && canViewAgentLogs && (
                        <div className={classes.logsContainer}>
                            {dummyLogs.map((log, index) => (
                                <div key={index} className={classes.logItem}>
                                    <div className={classes.logHeader}>
                                        <span className={classes.logAgentName}>{log.agentName}</span>
                                        <span className={classes.logTimestamp}>
                                            {new Date(log.timestamp).toLocaleTimeString('tr-TR', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                            })}
                                        </span>
                                    </div>
                                    <div className={classes.logContent}>{log.content}</div>
                                    <div className={classes.logType}>Type: {log.agentLogType}</div>
                                </div>
                            ))}
                        </div>
                    )} */}
                </>
            )}

            {/* Feedback Modal */}
            {message.id && agentContext?.selectedAgentId && (
                <FeedbackModal
                    isOpen={isFeedbackModalOpen}
                    onClose={handleFeedbackModalClose}
                    agentId={agentContext.selectedAgentId}
                    sessionId={selectedId}
                    messageId={message.id}
                    existingFeedback={existingFeedback}
                />
            )}
        </div>
    );
};
